import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import axios from "axios";
import { UserPlus, Search, Loader2, Phone, Mail, Clock, ChevronRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MinijobWarnung } from "@/components/crm/controlling/shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ART_COLORS = {
  Minijob: "bg-purple-100 text-purple-800",
  Teilzeit: "bg-cyan-100 text-cyan-800",
  Vollzeit: "bg-green-100 text-green-800",
  Sonstige: "bg-slate-100 text-slate-700",
};

const monatKurz = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("de-DE", { month: "short", year: "2-digit" });
const monatLang = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("de-DE", { month: "long", year: "numeric" });
const EMPTY_FORM = { vorname: "", nachname: "", telefon: "", email: "", beschaeftigungsart: "Minijob", sollstunden: 0, beschaeftigungsbeginn: "", beschaeftigungsende: "", aktiv: true, aufgaben_ab: "", rentenverzicht: false };

export default function CRMMitarbeiter({ onSelect, editMid, onSaved, onNeu }) {
  const { user } = useAuth();
  const [mitarbeiter, setMitarbeiter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("alle");
  const [filterArt, setFilterArt] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [istAktuell, setIstAktuell] = useState(null);
  const [mjWarnung, setMjWarnung] = useState(null);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${user?.token}` };

  useEffect(() => { if (editMid) { axios.get(`${API}/mitarbeiter/${editMid}`, { headers }).then((r) => { openEdit(r.data); setLoading(false); }).catch(() => { toast.error("Mitarbeiter nicht gefunden"); setLoading(false); }); } else fetchMitarbeiter(); }, [search, filterStatus, filterArt, editMid]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMitarbeiter = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (filterStatus !== "alle") params.set("status", filterStatus);
      if (filterArt) params.set("art", filterArt);
      const res = await axios.get(`${API}/mitarbeiter?${params}`, { headers });
      setMitarbeiter(res.data);
    } catch { toast.error("Fehler beim Laden."); }
    setLoading(false);
  };

  const openCreate = () => { if (onNeu) return onNeu(); setForm(EMPTY_FORM); setIstAktuell(null); setMjWarnung(null); setEditId(null); setShowForm(true); };
  const openEdit = (m) => {
    setForm({ vorname: m.vorname, nachname: m.nachname, telefon: m.telefon || "", email: m.email || "", beschaeftigungsart: m.beschaeftigungsart, sollstunden: m.sollstunden, beschaeftigungsbeginn: m.beschaeftigungsbeginn || "", beschaeftigungsende: m.beschaeftigungsende || "", aktiv: m.aktiv, aufgaben_ab: m.aufgaben_ab || "", rentenverzicht: !!m.rentenverzicht, strasse: m.strasse || "", hausnummer: m.hausnummer || "", plz: m.plz || "", ort: m.ort || "", stundenlohn_brutto: m.stundenlohn_brutto ?? "", vertrag_monatsstunden: m.vertrag_monatsstunden ?? "" });
    setIstAktuell(m.ist_stunden_aktuell || null);
    setMjWarnung(m.minijob_warnung || null);
    setEditId(m.id);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.vorname || !form.nachname) { toast.error("Vor- und Nachname erforderlich."); return; }
    setSaving(true);
    const payload = { ...form, aufgaben_ab: form.aufgaben_ab || null, stundenlohn_brutto: form.stundenlohn_brutto === "" || form.stundenlohn_brutto == null ? null : Number(form.stundenlohn_brutto), vertrag_monatsstunden: form.vertrag_monatsstunden === "" || form.vertrag_monatsstunden == null ? null : Number(form.vertrag_monatsstunden) };
    try {
      if (editId) {
        await axios.patch(`${API}/mitarbeiter/${editId}`, payload, { headers });
        toast.success("Mitarbeiter aktualisiert");
      } else {
        await axios.post(`${API}/mitarbeiter`, payload, { headers });
        toast.success("Mitarbeiter erstellt");
      }
      if (editMid) { onSaved?.(); } else { setShowForm(false); fetchMitarbeiter(); }
    } catch { toast.error("Fehler beim Speichern."); }
    setSaving(false);
  };

  const toggleStatus = async (m) => {
    try {
      await axios.patch(`${API}/mitarbeiter/${m.id}`, { aktiv: !m.aktiv }, { headers });
      toast.success(m.aktiv ? "Mitarbeiter deaktiviert" : "Mitarbeiter aktiviert");
      if (editMid) { setForm((f) => ({ ...f, aktiv: !m.aktiv })); onSaved?.(); } else fetchMitarbeiter();
    } catch { toast.error("Fehler."); }
  };

  const formular = (
            <form onSubmit={handleSave} className={`space-y-4 ${editMid ? "" : "p-5"}`} data-testid="ma-stammdaten-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1 block">Vorname *</Label>
                  <Input value={form.vorname} onChange={e => setForm({...form, vorname: e.target.value})} placeholder="Vorname" className="h-11 rounded-xl" required data-testid="ma-vorname" />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1 block">Nachname *</Label>
                  <Input value={form.nachname} onChange={e => setForm({...form, nachname: e.target.value})} placeholder="Nachname" className="h-11 rounded-xl" required data-testid="ma-nachname" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1 block">Telefon</Label>
                  <Input value={form.telefon} onChange={e => setForm({...form, telefon: e.target.value})} placeholder="Telefonnummer" className="h-11 rounded-xl" data-testid="ma-telefon" />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1 block">E-Mail</Label>
                  <Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="E-Mail" className="h-11 rounded-xl" data-testid="ma-email" />
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="ma-anschrift">
                {[["strasse", "Straße"], ["hausnummer", "Hausnummer"], ["plz", "PLZ"], ["ort", "Ort"]].map(([k, l]) => <div key={k}><Label className="text-sm font-semibold text-slate-700 mb-1 block">{l}</Label><Input value={form[k] || ""} onChange={e => setForm({...form, [k]: e.target.value})} className="h-11 rounded-xl" data-testid={`ma-${k}`} /></div>)}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-sm font-semibold text-slate-700 mb-1 block">Bruttostundenlohn (€, Vertrag)</Label><Input type="number" step="0.01" min="0" value={form.stundenlohn_brutto ?? ""} onChange={e => setForm({...form, stundenlohn_brutto: e.target.value})} className="h-11 rounded-xl" data-testid="ma-stundenlohn" /></div>
                {form.beschaeftigungsart === "Teilzeit" && <div><Label className="text-sm font-semibold text-slate-700 mb-1 block">Vereinbarte Monatsstunden (Vertrag)</Label><Input type="number" step="0.5" min="0" value={form.vertrag_monatsstunden ?? ""} onChange={e => setForm({...form, vertrag_monatsstunden: e.target.value})} className="h-11 rounded-xl" data-testid="ma-monatsstunden" /></div>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1 block">Beschäftigungsart</Label>
                  <select value={form.beschaeftigungsart} onChange={e => setForm({...form, beschaeftigungsart: e.target.value})}
                    className="w-full h-11 rounded-xl border border-slate-200 px-3 text-sm" data-testid="ma-art">
                    <option value="Minijob">Minijob</option>
                    <option value="Teilzeit">Teilzeit</option>
                    <option value="Vollzeit">Vollzeit</option>
                    <option value="Sonstige">Sonstige</option>
                  </select>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1 block">Erster Aufgabenmonat (Checkliste)</Label>
                  <Input type="month" value={form.aufgaben_ab} onChange={e => setForm({...form, aufgaben_ab: e.target.value})} className="h-11 rounded-xl" data-testid="ma-aufgaben-ab" />
                </div>
                {form.beschaeftigungsart !== "Minijob" ? (
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-1 block">Sollstunden / Monat</Label>
                    <Input type="number" step="0.5" min="0" value={form.sollstunden} onChange={e => setForm({...form, sollstunden: parseFloat(e.target.value) || 0})}
                      className="h-11 rounded-xl" data-testid="ma-sollstunden" />
                  </div>
                ) : (
                  <label className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 h-11 cursor-pointer self-end" data-testid="ma-rentenverzicht-label">
                    <input type="checkbox" checked={!!form.rentenverzicht} onChange={e => setForm({...form, rentenverzicht: e.target.checked})} className="h-4 w-4 rounded" data-testid="ma-rentenverzicht" />
                    <span className="text-sm font-semibold text-slate-700">Rentenverzicht</span>
                  </label>
                )}
              </div>
              {form.beschaeftigungsart === "Minijob" && <p className="text-xs text-slate-500 -mt-2">Rentenverzicht = Befreiung von der Rentenversicherungspflicht im Minijob (vom Mitarbeiter schriftlich beantragt).</p>}
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 flex items-center gap-3" data-testid="ma-ist-stunden">
                <Clock className="h-4 w-4 text-slate-500 shrink-0" />
                <div className="text-sm">
                  <span className="font-semibold text-slate-800">Ist-Stunden aus dem Controlling: </span>
                  {istAktuell ? <><span className="font-bold text-slate-900">{istAktuell.ist_stunden} Std.</span> <span className="text-xs text-slate-500">Stand {monatLang(istAktuell.monat)}</span></> : <span className="text-slate-500">noch kein Monat mit Ist-Stunden erfasst</span>}
                  {form.beschaeftigungsart === "Minijob" && <p className="text-xs text-slate-500 mt-0.5">Für Minijobber werden keine Sollstunden geführt – maßgeblich sind die im Controlling erfassten Ist-Stunden (inkl. Fahrtzeiten).</p>}
                </div>
              </div>
              {form.beschaeftigungsart === "Minijob" && mjWarnung && <div className={`rounded-xl border p-3 text-sm flex items-start gap-2 ${mjWarnung.stufe === "ueberschritten" || mjWarnung.stufe === "prognose" ? "bg-red-50 border-red-200 text-red-900" : "bg-amber-50 border-amber-200 text-amber-900"}`} data-testid="ma-minijob-warnung"><MinijobWarnung w={mjWarnung} /><span className="text-xs">{mjWarnung.text}</span></div>}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1 block">Beginn</Label>
                  <Input type="date" value={form.beschaeftigungsbeginn} onChange={e => setForm({...form, beschaeftigungsbeginn: e.target.value})} className="h-11 rounded-xl" data-testid="ma-beginn" />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1 block">Ende (optional)</Label>
                  <Input type="date" value={form.beschaeftigungsende} onChange={e => setForm({...form, beschaeftigungsende: e.target.value})} className="h-11 rounded-xl" data-testid="ma-ende" />
                </div>
              </div>
              {editId && <p className="text-xs text-slate-500 rounded-xl bg-slate-50 border border-slate-200 p-3" data-testid="zugang-hinweis">Login, Passwort und Rolle (Mitarbeiter/Teamleitung) werden unter <b>Einstellungen → Zugänge &amp; Rollen</b> verwaltet.</p>}
              {editId && (
                <div className="flex items-center gap-3 pt-2">
                  <button type="button" onClick={() => toggleStatus({ id: editId, aktiv: form.aktiv })}
                    className={`text-sm font-semibold px-4 py-2 rounded-xl transition-all ${form.aktiv ? "bg-red-50 text-red-600 hover:bg-red-100" : "bg-green-50 text-green-600 hover:bg-green-100"}`}>
                    {form.aktiv ? "Deaktivieren" : "Wieder aktivieren"}
                  </button>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                {!editMid && <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1 rounded-xl">Abbrechen</Button>}
                <Button type="submit" disabled={saving} className="flex-1 bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl" data-testid="ma-save-btn">
                  {saving ? "Speichern..." : editId ? "Speichern" : "Erstellen"}
                </Button>
              </div>
            </form>
  );

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-cyan-700" /></div>;
  if (editMid) return <div data-testid="ma-stammdaten">{editId ? formular : null}</div>;

  return (
    <div data-testid="crm-mitarbeiter">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-['Manrope'] text-2xl font-bold text-slate-900">Mitarbeiter</h1>
          <p className="text-sm text-slate-500 mt-1">{mitarbeiter.length} Mitarbeiter</p>
        </div>
        <Button onClick={openCreate} data-testid="add-mitarbeiter-btn" className="bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl">
          <UserPlus className="h-4 w-4 mr-2" /> Neue Einstellung (Mitarbeiter anlegen)
        </Button>
      </div>

      {/* Search + Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Suche nach Name..." className="pl-10 h-11 rounded-xl" data-testid="search-mitarbeiter" />
        </div>
        <div className="flex gap-2 text-sm flex-wrap">
          {["alle", "aktiv", "inaktiv"].map(s => (
            <button key={s} onClick={() => setFilterStatus(s)} data-testid={`filter-status-${s}`}
              className={`px-3 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${filterStatus === s ? "bg-cyan-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {s === "alle" ? "Alle" : s === "aktiv" ? "Aktiv" : "Inaktiv"}
            </button>
          ))}
          <select value={filterArt} onChange={e => setFilterArt(e.target.value)} data-testid="filter-art" className="px-3 py-2 rounded-lg bg-slate-100 text-sm font-medium text-slate-600 border-0">
            <option value="">Alle Arten</option>
            <option value="Minijob">Minijob</option>
            <option value="Teilzeit">Teilzeit</option>
            <option value="Vollzeit">Vollzeit</option>
            <option value="Sonstige">Sonstige</option>
          </select>
        </div>
      </div>

      {/* List */}
      {mitarbeiter.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <UserPlus className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Noch keine Mitarbeiter angelegt.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {mitarbeiter.map(m => (
            <div key={m.id} className={`bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-all cursor-pointer ${!m.aktiv ? "opacity-60" : ""}`}
              onClick={() => (onSelect ? onSelect(m) : openEdit(m))} data-testid={`mitarbeiter-${m.id}`}>
              <div className="flex items-center justify-between gap-2 min-w-0">
                <div className="flex items-center gap-4 min-w-0">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold ${m.aktiv ? "bg-cyan-100 text-cyan-700" : "bg-slate-100 text-slate-500"}`}>
                    {m.vorname[0]}{m.nachname[0]}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{m.vorname} {m.nachname}</p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 mt-0.5 min-w-0">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ART_COLORS[m.beschaeftigungsart] || ART_COLORS.Sonstige}`}>{m.beschaeftigungsart}</span>
                      {m.beschaeftigungsart !== "Minijob" && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Soll {m.sollstunden} Std./Monat</span>}
                      <span className="flex items-center gap-1" data-testid={`ist-stunden-${m.id}`}><Clock className="h-3 w-3" />{m.ist_stunden_aktuell ? <>Ist {m.ist_stunden_aktuell.ist_stunden} Std. <span className="text-slate-400">({monatKurz(m.ist_stunden_aktuell.monat)})</span></> : <span className="text-slate-400">Ist-Stunden: noch kein Controlling</span>}</span>
                      {m.beschaeftigungsart === "Minijob" && m.rentenverzicht && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600" data-testid={`rentenverzicht-badge-${m.id}`}>Rentenverzicht</span>}
                      <MinijobWarnung w={m.minijob_warnung} klein />
                      {m.zugang && <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${m.zugang.aktiv ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`} data-testid={`zugang-badge-${m.id}`}>Login {m.zugang.aktiv ? "aktiv" : "deaktiviert"}</span>}
                      {m.telefon && <span className="hidden sm:flex items-center gap-1"><Phone className="h-3 w-3" />{m.telefon}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${m.aktiv ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                    {m.aktiv ? "Aktiv" : "Inaktiv"}
                  </span>
                  <ChevronRight className="h-4 w-4 text-slate-400" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="mitarbeiter-form">
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <h2 className="font-['Manrope'] text-lg font-bold text-slate-900">{editId ? "Mitarbeiter bearbeiten" : "Neuer Mitarbeiter"}</h2>
              <button onClick={() => setShowForm(false)} className="p-1 rounded-lg hover:bg-slate-100"><X className="h-5 w-5 text-slate-500" /></button>
            </div>
            {formular}
          </div>
        </div>
      )}
    </div>
  );
}
